import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ITableResponsive } from 'src/app/domain/ITableResponsive';
import { IPartner } from 'src/app/domain/registration/models/IPartner';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import {
  COLUMNS_TO_SHOW_MOBILE,
  COLUMNS_TO_SHOW_WEB,
  TABLE_CONFIG_COLUMNS,
} from '../crud-config';
import { PartnerService } from '../crud-service';
import { Router } from '@angular/router';
import { AbstractCrudIndex } from 'src/app/shared/components/crud/abstract-crud-index';
import { take } from 'rxjs';
import { MessagesEnum } from 'src/app/domain/enums/messages.enum';
import { DialogConfirmComponent } from 'src/app/shared/components/dialog-confirm/dialog-confirm.component';
import { GlobalUtils } from 'src/app/shared/global-utils';
import { UserService } from '../../users/crud.service';
import { AuthService } from 'src/app/views/auth/shared/auth.service';
import { PermissionService } from 'src/app/shared/permission.service';
import { PartnerSettingsComponent } from './partner-settings/partner-settings.component';
import { TypeIndexEnum } from 'src/app/domain/registration/enums/type-index.enum';
import { CustomerDataDialogComponent } from '../customer-data-dialog/customer-data-dialog.component';
import { FormGroup } from '@angular/forms';
import { PartnerContractGenerateComponent } from '../components/contract-generate/contract-generate.component';
import { firstValueFrom } from 'rxjs';
import { ContractGenerationService } from '../../contracts/services/contract-generation.service';
import { ContractsService } from '../../contracts/crud-service';
import { ContractsListDialogComponent } from '../../contracts/components/contract-dialog/contract-list-modal/contracts-list-dialog.component';
import moment from 'moment';
import { AccountDashboardService } from '../../accounts-dashboard/account-dashboard.service';
import { ReportPreviewComponent } from '../../customers/crud-index/report-preview/report-preview.component';
import { UserGroupPermissionsComponent } from '../../user-group-permissions/user-group-permissions.component';
import { ButtonConfig } from 'src/app/domain/registration/models/ButtonConfig';

@Component({
  selector: 'app-partner-index',
  templateUrl: './crud-index.component.html',
  styleUrls: ['./crud-index.component.sass'],
  providers: [],
})
export class PartnerCrudIndex
  extends AbstractCrudIndex<IPartner>
  implements ITableResponsive {
  override configColumns = TABLE_CONFIG_COLUMNS;
  override COLUMNS_TO_SHOW_MOBILE = COLUMNS_TO_SHOW_MOBILE;
  override COLUMNS_TO_SHOW_WEB = COLUMNS_TO_SHOW_MOBILE;
  filterFormValues: any = null; // Propriedade para armazenar valores do formulário de filtro
  protected accountDashboardService = inject(AccountDashboardService);


  // Flag para o estado "Selecionar Todos" (mesmo que não usada nas ações atuais, para consistência)
  isSelectAllActive: boolean = false;

  editParams = [
    'image',
    'cnpj',
    'companyName',
    'tradingName',
    'stateRegistration',
    'municipalRegistration',
    'phone',
    'cellPhone',
    'email',
  ];

  userType: string = '';
  currentTab: string = 'partners';
  partnerButtons: ButtonConfig[] = [
    {
      icon: 'admin_panel_settings',
      tooltip: 'Permissão',
      class: 'btn-permission',
      action: 'permissions',
      isInsideMenu: true,
    },
  ];

  constructor(
    public override dialog: MatDialog,
    public override service: PartnerService,
    public override toastService: ToastService,
    public override mediaObserver: MediaObserver,
    public usersService: UserService,
    public override router: Router,
    protected cdr: ChangeDetectorRef,
    protected authService: AuthService,
    public permissionService: PermissionService,
    public contractsService: ContractsService,
    public contractGenerationService: ContractGenerationService,
  ) {
    super(dialog, service, toastService, mediaObserver, router);

    this.userType = this.authService.isPartner() ? 'partner' : 'admin';
    this.currentTab = 'partners';

    this.title = service.pluralTitle.toUpperCase();
    mediaObserver.asObservable().subscribe((res) => {
      this.is_mobile = res[0].mqAlias === 'sm' || res[0].mqAlias === 'xs';
      this.displayedColumns = this.is_mobile
        ? this.COLUMNS_TO_SHOW_MOBILE
        : this.COLUMNS_TO_SHOW_WEB;
    });
  }

  public override loadList() {
    let params = this.params;

    this.service.listAll(params).subscribe((dataWithPagination: any) => {
      const [data, length] = dataWithPagination;
      this.length = length;

      const initialData = data.map((item: any) => {
        item['document'] = item.cnpj || item.cpf;
        item['name'] = item.companyName || item.name;
        item['hideEdit'] = !item.allowEdit;
        item['hideDelete'] = !item.allowDelete;
        item['image'] = item.image;
        item.code = `${item.code}`.padStart(4, '0');

        item['isContractGenerated'] = 'LOADING';
        item['isContractSigned'] = 'LOADING';


        return item;
      });

      this.dataSource = new MatTableDataSource<any>(initialData);
      const promises = initialData.map(async (item: any) => {
        try {
          const contractStatus = await firstValueFrom(this.contractsService.getContractsByPartner(item.id));

          if (contractStatus && Array.isArray(contractStatus)) {
            const totalContracts = contractStatus.length;
            const signedContracts = contractStatus.filter(c => c.status === 'signed').length;

            item['isContractGenerated'] = totalContracts > 0 ? 'SIM' : 'NÃO';

            if (totalContracts > 0) {
              if (signedContracts === totalContracts) {
                item['isContractSigned'] = 'SIM';
              } else if (signedContracts > 0) {
                item['isContractSigned'] = `PARCIAL (${signedContracts}/${totalContracts})`;
              } else {
                item['isContractSigned'] = 'NÃO';
              }
            } else {
              item['isContractSigned'] = 'NÃO'; // Sem contratos, então não há assinados
            }
          } else {
            item['isContractGenerated'] = 'NÃO';
            item['isContractSigned'] = 'NÃO';
          }
        } catch (error) {
          console.error(`Erro ao verificar contratos do parceiro ${item.id}:`, error);
          item['isContractGenerated'] = 'ERRO';
          item['isContractSigned'] = 'ERRO';
        }
        return item;
      });

      Promise.all(promises).then(processedData => {
        this.dataSource.data = processedData;
        if (this.params.filter && this.params.filter.trim() !== '') {
          const filterValue = this.params.filter.toLowerCase();
          this.dataSource.filter = filterValue;
          this.dataSource.filterPredicate = (data: any, filter: string) => {
            const name = (data.name || '').toLowerCase();
            const document = (data.document || '').toLowerCase();
            const email = (data.email || '').toLowerCase();
            const code = (data.code || '').toLowerCase();
            const isContractGenerated = (data.isContractGenerated || '').toLowerCase();
            const isContractSigned = (data.isContractSigned || '').toLowerCase();

            return name.includes(filter) ||
              document.includes(filter) ||
              email.includes(filter) ||
              code.includes(filter) ||
              isContractGenerated.includes(filter) ||
              isContractSigned.includes(filter);
          };
        }
        this.cdr.detectChanges();
      });
    });
  }

  public override submitAdvancedFilter($event: any) {
    throw new Error('Method not implemented.');
  }

  public ngAfterViewInit(): void {
    this.loadList();
  }

  public _openEdit($event: any) {
    this.router.navigate(['partners/edit', $event.id], {
      state: { editParams: this.editParams }
    });
  }

  public _openNew($event: any) {
    this.router.navigate(['partners/new']);
  }

  public _openShow($event: any) {
    this.router.navigate(['partners/show', $event.id]);
  }

  public lock(partner: any) {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      data: { title: `Bloquear parceiro: ${partner.name || partner.companyName}` },
      component: DialogConfirmComponent,
      callBack: (data: any) => {
        if (data?.result) {
          this.service
            .changeActiveStatus(partner.id!, false)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.toastService.showSuccess(MessagesEnum.PARTNER_LOCK_SUCCESS);
                this.loadList();
              },
              error: (e) => console.log(e),
            });
        }
      },
    });
  }

  public unlock(partner: any) {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      data: { title: `Desbloquear parceiro: ${partner.name || partner.companyName}` },
      component: DialogConfirmComponent,
      callBack: (data: any) => {
        if (data?.result) {
          this.service
            .changeActiveStatus(partner.id!, true)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.toastService.showSuccess(
                  MessagesEnum.PARTNER_UNLOCK_SUCCESS
                );
                this.loadList();
              },
              error: (e) => console.log(e),
            });
        }
      },
    });
  }

  public generateContract() {
    const selectedPartner = this.getSelectedPartner();
    if (!selectedPartner) {
      this.toastService.showWarn('Selecione um parceiro para gerar o contrato');
      return;
    }

    this.contractGenerationService.getAllTemplates().subscribe((template) => {
      const dialogRef = this.dialog.open(PartnerContractGenerateComponent, {
        width: '50%',
        data: {
          entity: selectedPartner,
          type: 'partner',
          template,
        },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          console.log('Modal de geração de contrato fechado com resultado:', result);
          this.loadList();
        } else {
          console.log('Modal de geração de contrato fechado sem resultado (cancelado ou erro).');
        }
      });
    });
  }

  public openContractsDialog(element: any) {
    this.dialog.open(ContractsListDialogComponent, {
      width: '80%',
      data: {
        type: 'partner',
        entityInfo: {
          id: element.id,
          name: element.name || element.companyName,
          document: element.document,
          email: element.email,
          phone: element.phone || element.cellPhone
        }
      },
    });
  }

  public active(partner: any) {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      data: {
        title: `Ativar parceiro: ${partner.name || partner.companyName}`,
      },
      component: DialogConfirmComponent,
      callBack: (data: any) => {
        if (data?.result) {
          this.service
            .changeActiveStatus(partner.id!, true)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.toastService.showSuccess(
                  MessagesEnum.PARTNER_ACTIVE_SUCCESS
                );
                this.loadList();
              },
              error: (e) => console.log(e),
            });
        }
      },
    });
  }

  public inactive(partner: any) {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      data: {
        title: `Desativar parceiro: ${partner.name || partner.companyName}`,
      },
      component: DialogConfirmComponent,
      callBack: (data: any) => {
        if (data?.result) {
          this.service
            .changeActiveStatus(partner.id!, false)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.toastService.showSuccess(
                  MessagesEnum.PARTNER_INACTIVE_SUCCESS
                );
                this.loadList();
              },
              error: (e) => console.log(e),
            });
        }
      },
    });
  }

  public lockPeople(partner: any) {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      data: { title: `Bloquear carteira clientes do parceiro: ${partner.name || partner.companyName}` },
      component: DialogConfirmComponent,
      callBack: (data: any) => {
        if (data?.result) {
          this.service
            .changeWalletActiveStatus(partner.id!, false)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.toastService.showSuccess(MessagesEnum.PARTNER_LOCK_PEOPLE_SUCCESS);
                this.loadList();
              },
              error: (e) => console.log(e),
            });
        }
      },
    });
  }

  public unlockPeople(partner: any) {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      data: { title: `Desbloquear carteira de clientes do parceiro: ${partner.name || partner.companyName}` },
      component: DialogConfirmComponent,
      callBack: (data: any) => {
        if (data?.result) {
          this.service
            .changeWalletActiveStatus(partner.id!, true)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.toastService.showSuccess(
                  MessagesEnum.PARTNER_UNLOCK_PEOPLE_SUCCESS
                );
                this.loadList();
              },
              error: (e) => console.log(e),
            });
        }
      },
    });
  }

  private getSelectedPartner() {
    if (!this.selection || !this.selection.selected || this.selection.selected.length !== 1) {
      return null;
    }
    return this.selection.selected[0];
  }

  // reset password
  public key(partner: any) {
    if (!partner.users || partner.users.length === 0) {
      this.toastService.showWarn(
        MessagesEnum.PARTNER_WITHOUT_GENERATED_ACCESS
      );
      return;
    }

    GlobalUtils.openDialog({
      dialog: this.dialog,
      data: { title: `Gerar nova senha para o parceiro: ${partner.name || partner.companyName}` },
      component: DialogConfirmComponent,
      callBack: (data: any) => {
        if (data?.result) {
          this.usersService
            .resetPassword(partner.id!)
            .pipe(take(1))
            .subscribe({
              next: (response: any) => {
                const email = response?.email || partner.email || '';
                const password = response?.password || '';
                this.toastService.showSuccess(
                  `Anote as novas credenciais do parceiro:<br><br>` +
                  `<b>Usuário:</b> ${email}<br>` +
                  `<b>Senha:</b> ${password}`,
                  {
                    ...this.toastService.modalProps,
                    title: 'Senha redefinida com sucesso',
                    enableHtml: true,
                    confirmButtonText: 'OK',
                    iconColor: '#4CAF50',
                  }
                );
                this.loadList();
              },
              error: (error: any) => {
                const raw = error?.error?.message || error?.message;
                const message = Array.isArray(raw) ? raw.join('; ') : (raw || 'Não foi possível redefinir a senha do parceiro.');
                this.toastService.showError(message);
              },
            });
        }
      },
    });
  }

  public generateAccess($event: any) {
    if ($event.users && $event.users.length > 0) {
      this.toastService.showWarn(
        MessagesEnum.PARTNER_WITH_GENERATED_ACCESS
      );
      return;
    }

    GlobalUtils.openDialog({
      dialog: this.dialog,
      data: { title: `Gerar acesso para o parceiro: ${$event.name || $event.companyName}` },
      component: DialogConfirmComponent,
      callBack: (data: any) => {
        if (data?.result) {
          this.usersService
            .generateAcessToPartner($event.id!)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.toastService.showSuccess(MessagesEnum.PARTNER_GENERATE_ACCESS_SUCCESS);
                this.loadList();
              },
              error: (error: any) => {
                const raw = error?.error?.message || error?.message;
                const message = Array.isArray(raw) ? raw.join('; ') : (raw || 'Não foi possível gerar o acesso do parceiro.');
                this.toastService.showError(message);
              },
            });
        }
      },
    });
  }

  public navigateToCommissionsDashboard(partner?: any) {
    if (partner) {
      // Se um parceiro foi selecionado, navegue para o dashboard com os parâmetros do parceiro
      this.router.navigate(['/partner-dashboard'], {
        queryParams: {
          partnerId: partner.id,
          partnerName: partner.name || partner.companyName
        }
      });
    } else {
      // Se nenhum parceiro foi selecionado, navegue para o dashboard sem filtros
      this.router.navigate(['/partner-dashboard']);
    }
  }

  public navigateToCustomerAccess() {
    // Navegar para a página de acessos de clientes
    // Se for um parceiro logado, pode incluir filtros específicos no futuro
    const isPartnerUser = this.authService.isPartner();

    if (isPartnerUser) {
      // Para parceiros, navegar com contexto específico se necessário
      this.router.navigate(['/customer-acess'], {
        queryParams: {
          partnerContext: 'true'
        }
      });
    } else {
      // Para administradores, navegar normalmente
      this.router.navigate(['/customer-acess']);
    }
  }

  public openSettings($event: any) {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      dialogConfig: {
        width: 'auto',
        minWidth: '400px',
        height: 'auto',
        minHeight: '400px',
        enterAnimationDuration: '0ms',
        exitAnimationDuration: '0ms',
        disableClose: false,
      },
      component: PartnerSettingsComponent,
      data: { type: TypeIndexEnum.CRUD, select: true, params: this.params },
      callBack: (data: any) => { },
    });
  }

  public handlePartnerAction(event: { action: string; element: any }): void {
    if (event.action !== 'permissions') return;
    this.dialog.open(UserGroupPermissionsComponent, {
      width: '90%',
      maxWidth: '1200px',
      data: {
        type: TypeIndexEnum.CRUD,
        select: true,
        element: null,
        partner: event.element,
      },
    });
  }

  public checkCustomerDocument(field: any, form: FormGroup): void {
    const document = form.get(field.name)?.value;
    if (!document) return;

    this.service.checkExistingCustomer(document).pipe(take(1)).subscribe({
      next: (customer) => {
        if (customer) {
          const dialogRef = this.dialog.open(CustomerDataDialogComponent, {
            width: '400px',
            data: { customer }
          });

          dialogRef.afterClosed().subscribe(result => {
            if (result) {
              // Fill form with customer data
              form.patchValue({
                // Basic information
                name: customer.name,
                tradingName: customer.tradingName,
                companyName: customer.companyName,
                cnpj: customer.cnpj,
                cpf: customer.cpf,
                stateRegistration: customer.stateRegistration,
                municipalRegistration: customer.municipalRegistration,
                generalRegistration: customer.generalRegistration,

                // Contact information
                phone: customer.phone,
                cellPhone: customer.cellPhone,
                email: customer.email,
                webSite: customer.webSite,

                // Address information
                cep: customer.cep,
                address: customer.address,
                addressComplement: customer.addressComplement,
                addressNumber: customer.addressNumber,
                neighborhood: customer.neighborhood,
                state: customer.state,
                city: customer.city,
                country: customer.country,
                addressObservation: customer.addressObservation,

                // Additional fields
                image: customer.image,
                birthday: customer.birthday,
                mainActivity: customer.mainActivity,
                consultantCommission: customer.consultantCommission,
                partnerCommission: customer.partnerCommission,
                representativeCommission: customer.representativeCommission,
                counterCommission: customer.counterCommission,
                unitCommission: customer.unitCommission,
                regime: customer.regime,
                fiscalEnvironment: customer.fiscalEnvironment,
                contributorType: customer.contributorType
              });
            }
          });
        }
      },
      error: (error) => {
        console.error('Error checking customer:', error);
      }
    });
  }

  // Adicionar/Sobrescrever onSelectionChange para lidar com o novo formato
  override onSelectionChange(selectionEvent: { selectAll: boolean, items?: any[] }) {
    console.log('[PartnerCrudIndex] Event received from table-responsive:', selectionEvent);

    if (selectionEvent && typeof selectionEvent.selectAll === 'boolean') {
      this.isSelectAllActive = selectionEvent.selectAll;

      if (this.isSelectAllActive) {
        this.selection = { selected: [], selectAll: true };
        console.log('[PartnerCrudIndex] SelectAll is ACTIVE. Individual selection cleared.');
      } else {
        this.selection = { selected: selectionEvent.items || [], selectAll: false };
        console.log('[PartnerCrudIndex] SelectAll is INACTIVE. Updating individual selection.');
      }
    } else {
      console.warn('[PartnerCrudIndex] Unexpected event structure. Resetting state.', selectionEvent);
      this.isSelectAllActive = false;
      this.selection = { selected: [] };
    }

    console.log('[PartnerCrudIndex] Final state - isSelectAllActive:', this.isSelectAllActive);
    console.log('[PartnerCrudIndex] Final state - selected count:', this.selection?.selected?.length ?? 0);
    this.cdr.detectChanges(); // Notificar o Angular sobre a mudança na seleção para atualizar os [disabled] dos botões
  }

  // Método para resetar todos os filtros
  public resetFilter(): void {
    // Limpar valores do formulário
    this.filterFormValues = null;

    // Resetar estado de filtros
    this.isAdvancedFilterActive = false;
    this.lastAppliedFilter = null;
    this.params = { limit: 25, page: 0, filter: '' };

    // Recarregar lista com filtros limpos
    this.loadList();
  }

  public generateReports() {
    let items = this.selection?.selectAll ? this.dataSource.data : this.selection?.selected;
    if (items.length <= 0) {
      this.toastService.showInfo("Selecione pelo menos uma parcela");
      return;
    }
    const columns = [
      { header: 'Código', key: 'code', type: 'text' },
      { header: 'Nome', key: 'name', type: 'text' },
      { header: 'Cidade', key: 'city', type: 'text' },
      { header: 'D. Corte', key: 'cutoffDay', type: 'text' },
      { header: 'Comissão(%)', key: 'commission', type: 'text', align: 'center', nowrap: true },
      { header: 'Cadastro', key: 'createdAt', type: 'date', minWidth: '115px' },
    ];

    const rows = items.map((i: any) => ({
      code: i.code ?? '',
      name: i.name ?? i.companyName ?? i.tradingName ?? '',
      city: i.city ?? '',
      createdAt: i.createdAt ?? '',
      commission: Number(i.partnerCommission ?? 0) +
        Number(i.consultantCommission ?? 0) +
        Number(i.representativeCommission ?? 0) +
        Number(i.counterCommission ?? 0) +
        Number(i.unitCommission ?? 0),
      cutoffDay: i.cutoffDay ?? ''
    }));

    const payload = {
      title: 'Relatório de Parceiros',
      columns,
      rows,
      showTotal: false,
    };

    this.accountDashboardService
      .generateReportPdf(payload)
      .subscribe({
        next: (pdfBlob: Blob) => {

          const fileName = `relatorio-parceiros-${new Date().toISOString().slice(0, 10)}.pdf`;

          this.dialog.open(ReportPreviewComponent, {
            data: {
              blob: pdfBlob,
              fileName: fileName
            },
            width: '80vw',
            height: '90vh'
          });

          this.toastService.showSuccess("PRÉ-VISUALIZAÇÃO GERADA");
        },
        error: (err) => {
          console.error('Erro ao gerar relatório PDF:', err);
          this.toastService.showError("Erro ao gerar PDF", err);
        }
      });

  }

}
